import { useMemo } from "react";
import { Modal } from "@themuse/design-system";
import { EmployeeStory } from "interfaces/api/apiInterfaces";
import styles from "./EmployeeStoryModal.module.scss";

export type EmployeeStoryProps = {
    employee?: EmployeeStory;
    isOpen?: boolean;
    setIsOpen: (x: boolean) => void;
};

type QuoteRecord = {
    key: string;
    text: string;
};

export const EmployeeStoryModal = ({ employee, isOpen, setIsOpen }: EmployeeStoryProps) => {
    const { fullName, quotes } = useMemo(() => ({
        fullName: [employee?.first_name, employee?.last_name].filter(Boolean).join(" "),
        quotes: Array.from({ length: 3 }).reduce<QuoteRecord[]>((accum: QuoteRecord[], current, i) => {
            const str = employee && employee[`quote${i + 1}`];
            if (str) {
                accum.push({
                    key: `${employee.id}_quote${i + 1}`,
                    text: str.trim().replace(/^["'“”‘’]|["'“”‘’]$/g, ""),
                });
            }
            return accum;
        }, [] as QuoteRecord[]),
    }), [employee?.id]);

    return (
        <Modal
            closeButtonClassName={styles.modalClose}
            containerClassName={styles.dialog}
            contentClassName={styles.modalContent}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
        >
            <header className={styles.header}>
                {!!employee?.employee_photo && (
                    <div className={styles.portrait}>
                        <img
                            alt={fullName}
                            className={styles.portraitImg}
                            loading="lazy"
                            src={employee?.employee_photo}
                        />
                        <svg
                            className={styles.portraitGraphic}
                            height="40"
                            viewBox="0 0 42 40"
                            width="42"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M -160.5 20.5 L 32 20.5 C 141 20.6 228.5 -67.5 228.5 -175.5 L 229 -302.5"
                                stroke="currentColor"
                            />
                            <path
                                d="M 20 39.5 C 20.5 34.5 23 30 26.5 26.5 C 30 23 35 21 40 21 C 34.5 20.5 29.5 18.5 26
                                14.5 C 22 10.5 20 5.5 20 0 C 20 5.5 18 11 14 15 C 10 18.5 5 21 -0.5 21 C 5 21 9.5 23
                                13.5 26 C 17 30 19.5 34.5 20 39.5 Z"
                                fill="currentColor"
                            />
                        </svg>
                    </div>
                )}
                <div className={styles.headerContent}>
                    <h1 className={styles.title}>{fullName}</h1>
                    {!!employee?.job_title && <p className={styles.subheading}>{employee?.job_title}</p>}
                </div>
            </header>
            <div className={styles.mainBody}>
                <h2 className={styles.overviewTitle}>
                    {employee?.first_name}
                    &apos;s Story
                </h2>
                {!!employee?.description && <p className={styles.overviewText}>{employee?.description}</p>}
                {quotes.map(({ key, text }) => <blockquote className={styles.quote} key={key}>{text}</blockquote>)}
            </div>
        </Modal>
    );
};
